import { useMemo, useState } from 'react';
import { color, mono } from '../tokens';
import { type Layer, type Lane } from '../fixtures';
import { useSynth } from '../SynthesisContext';
import { asText, cleanLabel } from '../asText';
import ProposalModal from '../components/ProposalModal';
import { countOf, agree } from '../../../utils/pluralize';
import { ARCH_SEED, LANE_SEED, LAYER_DEFINITIONS, normalizeWorkflowArchitecture } from '../waNormalize';

const HOT = { bg: '#FEF3C7', border: '#FCD34D', fg: '#B45309' };

const DEFAULT_ARCH = ARCH_SEED;
const DEFAULT_LANES = LANE_SEED;

interface WAProps {
  proposalSeen?: boolean;
  onConfirmProposal?: () => void;
}

// A layer/lane is "hot" iff its own `hot` flag is set. We read that flag as DATA
// (phase4_gaps G4) — we never regex-parse the diagnostic prose to decide which
// layers are named.
function isHot(l: Layer | Lane) {
  return !!l.hot;
}

function Bar({ pct, hot }: { pct: number; hot: boolean }) {
  return (
    <div style={{ height: 8, borderRadius: 999, background: '#F1F5F9', overflow: 'hidden' }}>
      <div style={{
        width: `${pct}%`, height: '100%', borderRadius: 999,
        background: hot ? HOT.fg : color.purpleGrad,
      }} />
    </div>
  );
}

function LayerRow({ l, secondaryLabel, secondaryCount }: {
  l: Layer | Lane; secondaryLabel: string; secondaryCount: number;
}) {
  const hot = isHot(l);
  return (
    <div style={{
      background: hot ? HOT.bg : color.surface,
      border: `1px solid ${hot ? HOT.border : color.hairline}`,
      borderRadius: 10, padding: '12px 14px', marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: color.ink, flex: 1, minWidth: 0 }}>{cleanLabel(l.name)}</span>
        {hot && (
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.05em', padding: '2px 7px', borderRadius: 6,
            background: '#fff', color: HOT.fg, border: `1px solid ${HOT.border}`,
          }}>IN DIAGNOSTIC</span>
        )}
        <span style={{ fontSize: 13, fontFamily: mono, fontWeight: 700, color: color.ink }}>
          {l.primary_count}
        </span>
        <span style={{ fontSize: 11, fontFamily: mono, color: color.textFaint }}>
          {l.pct_primary}%
        </span>
      </div>
      <div style={{ marginTop: 8 }}><Bar pct={l.pct_primary} hot={hot} /></div>
      <div style={{ marginTop: 4, fontSize: 11, color: color.textFaint, lineHeight: 1.45 }}>
        {LAYER_DEFINITIONS[l.code] || ''}
      </div>
      <div style={{ marginTop: 6, fontSize: 11, fontFamily: mono, color: color.textMuted }}>
        {secondaryCount} {secondaryLabel}
      </div>
      <p style={{ margin: '8px 0 0', fontSize: 12, color: color.textMuted, lineHeight: 1.5 }}>{asText(l.rationale) || '—'}</p>
      {l.findings?.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {l.findings.map((f, i) => (
            <span key={i} style={{
              fontSize: 11, padding: '3px 8px', borderRadius: 6,
              background: color.pillMutedBg, color: color.text,
            }}>{asText(f)}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function TaxonomyControls({ foundationOn, onToggleFoundation }: {
  foundationOn: boolean; onToggleFoundation: () => void;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: color.textFaint, margin: '0 0 8px' }}>
        WORKFLOW ARCHITECTURE · default seed
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {DEFAULT_ARCH.map((l) => (
          <span key={l.code} style={{
            fontSize: 12, padding: '4px 9px', borderRadius: 7,
            background: color.pillMutedBg, color: color.text, border: `1px solid ${color.hairline}`,
          }}>{l.name} <span style={{ color: color.textFaint }}>· {l.note}</span></span>
        ))}
      </div>

      {/* Foundation opt-in — visible, never a hidden flag */}
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
        border: `1px solid ${foundationOn ? color.chipBorder : color.hairline}`,
        background: foundationOn ? color.pillActiveBg : color.surface,
        borderRadius: 10, padding: '10px 12px', marginBottom: 12,
      }}>
        <input type="checkbox" checked={foundationOn} onChange={onToggleFoundation} style={{ marginTop: 2 }} />
        <span>
          <span style={{ fontSize: 13, fontWeight: 600, color: color.ink }}>Add the Foundation layer</span>
          <span style={{ display: 'block', fontSize: 12, color: color.textMuted, marginTop: 2, lineHeight: 1.5 }}>
            Opt-in. For substrate findings (capacity, performance, hosting, security posture, AI compute)
            that would otherwise inflate Integration. Off by default.
          </span>
        </span>
      </label>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: color.textFaint, margin: '0 0 8px' }}>
        WORKFLOW OVERSIGHT · lanes
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {DEFAULT_LANES.map((l) => (
          <span key={l.code} style={{
            fontSize: 12, padding: '4px 9px', borderRadius: 7,
            background: color.pillMutedBg, color: color.text, border: `1px solid ${color.hairline}`,
          }}>{l.name} <span style={{ color: color.textFaint }}>· {l.note}</span></span>
        ))}
      </div>
    </div>
  );
}

export default function WorkflowArchitecture({ proposalSeen, onConfirmProposal }: WAProps) {
  const { workflowArchitecture: rawWa } = useSynth();
  const wa = useMemo(() => normalizeWorkflowArchitecture(rawWa), [rawWa]);
  const fixtureHasFoundation = wa.architecture.some((l) => l.code === 'foundation');
  const [foundationOn, setFoundationOn] = useState<boolean>(fixtureHasFoundation);
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);

  const showModal = proposalSeen === false;

  function toggleFoundation() {
    setFoundationOn((v) => !v);
    setDirty(true);
  }

  // Foundation off => its row is hidden. The re-tally of the distribution and the
  // diagnostic counts happens SERVER-SIDE (like a Mind Map merge) — never recomputed
  // in the browser, which is where the chip and the number would drift apart.
  const arch = [...wa.architecture]
    .filter((l) => l.code !== 'foundation' || foundationOn)
    .sort((a, b) => a.stack_position - b.stack_position);
  const lanes = wa.oversight;

  const hotPrimary =
    arch.filter(isHot).reduce((a, l) => a + l.primary_count, 0) +
    lanes.filter(isHot).reduce((a, l) => a + l.primary_count, 0);

  const dist = [...arch, ...lanes].filter((l) => l.primary_count > 0);
  const distTotal = dist.reduce((a, l) => a + l.primary_count, 0) || 1;

  return (
    <div>
      <ProposalModal
        open={showModal}
        title="Proposed workflow taxonomy (v1.2)"
        subtitle="TSG's default classifier: five architecture layers and three oversight lanes. Confirm it, or edit before the tab renders — including whether this engagement opts into the Foundation layer."
        onUse={() => onConfirmProposal?.()}
        onEdit={() => { onConfirmProposal?.(); setEditMode(true); }}
      >
        <TaxonomyControls foundationOn={foundationOn} onToggleFoundation={toggleFoundation} />
      </ProposalModal>

      {/* Header block */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: color.textFaint }}>TAB</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', color: color.purple,
          background: color.pillActiveBg, padding: '2px 8px', borderRadius: 6,
        }}>WORKFLOW ARCHITECTURE · v1.2</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
            Where the findings actually sit
          </h2>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: color.textMuted }}>
            Two orthogonal groups: how the workflow is <strong style={{ color: color.text }}>built</strong> (architecture stack)
            and how it is <strong style={{ color: color.text }}>watched</strong> (oversight lanes).
          </p>
        </div>
        <button
          onClick={() => setEditMode((v) => !v)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer',
            padding: '8px 12px', borderRadius: 8,
            background: editMode ? color.ink : color.surface, color: editMode ? '#fff' : color.text,
            border: `1px solid ${editMode ? 'transparent' : color.hairline}`,
          }}
        >
          Edit taxonomy
        </button>
      </div>
      <div style={{ height: 1, background: color.hairline, margin: '18px 0' }} />

      {/* Inline taxonomy editor */}
      {editMode && (
        <div style={{
          background: color.surfaceAlt, border: `1px solid ${color.hairline}`, borderRadius: 10,
          padding: '14px 16px', marginBottom: 16,
        }}>
          <TaxonomyControls foundationOn={foundationOn} onToggleFoundation={toggleFoundation} />
        </div>
      )}

      {/* Re-tally pending banner — server-side, never browser recompute */}
      {dirty && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: HOT.bg, border: `1px solid ${HOT.border}`, borderRadius: 8,
          padding: '8px 12px', marginBottom: 16, fontSize: 12.5, color: HOT.fg,
        }}>
          Taxonomy changed — the distribution and diagnostic counts <em>re-tally server-side</em>. Shown counts are the previous run until it returns.
        </div>
      )}

      {/* The diagnostic sentence — the tab's whole point */}
      <div style={{
        background: color.ink, color: '#fff', borderRadius: 14, padding: '20px 22px', marginBottom: 8,
      }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#94A3B8', marginBottom: 8 }}>
          THE DIAGNOSTIC
        </div>
        <p style={{ margin: 0, fontSize: 17, fontWeight: 600, lineHeight: 1.5 }}>
          {asText(wa.diagnostic).replace(/findings/gi, 'observations').replace(/finding /gi, 'observation ') || '(no diagnostic sentence generated yet)'}
        </p>
      </div>
      <div style={{ fontSize: 11, fontFamily: mono, color: color.textFaint, marginBottom: 20 }}>
        Flagged layers carry {hotPrimary} of {wa.total_findings} primaries · multi-valued rate{' '}
        {Math.round(wa.multi_valued_rate * 100)}%
        {wa.ai_component && wa.ai_component.tagged > 0 && wa.ai_component.tagged <= wa.total_findings
          ? ` · AI-tagged ${wa.ai_component.tagged} (${Math.round(wa.ai_component.rate * 100)}%)`
          : ''}
      </div>

      {/* Primary distribution bar (arch + oversight primaries sum to total) */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ height: 12, borderRadius: 999, overflow: 'hidden', display: 'flex' }}>
          {dist.map((l) => (
            <span
              key={l.code}
              title={`${l.name}: ${l.primary_count}`}
              style={{
                width: `${(l.primary_count / distTotal) * 100}%`,
                background: isHot(l) ? HOT.fg : color.purple,
                opacity: isHot(l) ? 1 : 0.35,
              }}
            />
          ))}
        </div>
        <div style={{ marginTop: 6, fontSize: 11, color: color.textFaint }}>
          {countOf(wa.total_findings, 'finding')} · {agree(wa.total_findings, 'it has', 'each has')} exactly one primary
        </div>
      </div>

      {!foundationOn && fixtureHasFoundation && (
        <div style={{ fontSize: 12, color: color.textMuted, marginBottom: 14 }}>
          Foundation layer not enabled — substrate findings fold into Integration.
        </div>
      )}

      {/* Two groups */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: color.ink, marginBottom: 10 }}>
            Workflow Architecture <span style={{ color: color.textFaint, fontWeight: 400 }}>· the stack</span>
          </div>
          {arch.map((l) => (
            <LayerRow key={l.code} l={l} secondaryLabel="as secondary" secondaryCount={(l as Layer).secondary_count} />
          ))}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: color.ink, marginBottom: 10 }}>
            Workflow Oversight <span style={{ color: color.textFaint, fontWeight: 400 }}>· the lanes</span>
          </div>
          {lanes.map((l) => (
            <LayerRow key={l.code} l={l} secondaryLabel="as tag" secondaryCount={(l as Lane).tag_count} />
          ))}
        </div>
      </div>
    </div>
  );
}
